import React from 'react';
import { Link } from 'react-router-dom';
import { Hammer, CheckCircle2, Clock, Shield, Wrench, ArrowRight } from 'lucide-react';
import BeforeAfterGallery from '../../components/BeforeAfterGallery';
import WorkGallery from '../../components/WorkGallery';

const DentRepair = () => {
  const beforeAfterPairs = [
    { before: '/siteimages/before 5.jpg', after: '/siteimages/after 5.jpg' },
    { before: '/siteimages/before 6.jpg', after: '/siteimages/after 6.jpg' },
    { before: '/siteimages/before 7.jpg', after: '/siteimages/after 7.jpg' },
    { before: '/siteimages/before 8.jpg', after: '/siteimages/after 8.jpg' },
  ];

  const workImages = [
    '/siteimages/image_0025.jpg',
    '/siteimages/image_0028.jpg',
    '/siteimages/image_0032.jpg',
    '/siteimages/image_0033.jpg',
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Hammer className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Dent Repair & Removal
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Professional dent removal services using advanced techniques to restore your vehicle to its original condition.
          </p>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Expert <span className="text-bmw-blue">Dent Removal</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                From minor door dings to major collision damage, our skilled technicians have the expertise 
                to repair dents of all sizes. We use both traditional panel beating techniques and modern 
                paintless dent removal (PDR) methods, depending on the type and location of the damage.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our dent repair process preserves your vehicle's original paint finish whenever possible, 
                maintaining its value and appearance. For more extensive damage, we combine panel work with 
                our expert paint refinishing services for seamless results.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-bmw-blue" />
                  Advanced Dent Repair Technology
                </h3>
                <p className="text-gray-600">
                  We utilize specialized tools and techniques including PDR, panel beating, and body filler 
                  application to achieve factory-quality repairs on all types of dents.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">What We Fix</h3>
                <ul className="space-y-3">
                  {[
                    'Door dings and parking lot dents',
                    'Hail damage',
                    'Shopping cart impacts',
                    'Minor collision damage',
                    'Creases and sharp dents',
                    'Body panel dents',
                    'Boot lid and bonnet dents',
                    'Quarter panel damage',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Free Dent Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Not sure if your dent can be repaired? Contact us for a free assessment and quote.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold text-dark-navy mb-12 text-center">
            Our <span className="text-bmw-blue">Repair Process</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Inspection',
                description: 'Detailed assessment of dent location, depth, and surrounding paint condition.',
              },
              {
                step: '02',
                title: 'Method Selection',
                description: 'Choosing the optimal repair technique - PDR, traditional repair, or combination.',
              },
              {
                step: '03',
                title: 'Dent Removal',
                description: 'Precise dent repair using specialized tools and expert craftsmanship.',
              },
              {
                step: '04',
                title: 'Finishing',
                description: 'Final paint touch-up and quality inspection for perfect results.',
              },
            ].map((process, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-5xl font-bold text-bmw-blue opacity-20 mb-3">{process.step}</div>
                <h3 className="text-xl font-bold text-dark-navy mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quick Service</h3>
              <p className="text-soft-grey">
                Most dent repairs completed in 1-3 days depending on damage severity.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Guaranteed Work</h3>
              <p className="text-soft-grey">
                All dent repairs backed by our workmanship guarantee for your peace of mind.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Wrench className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Expert Technicians</h3>
              <p className="text-soft-grey">
                Highly trained professionals with extensive dent repair experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <BeforeAfterGallery beforeAfterPairs={beforeAfterPairs} title="Dent Repair Results" />
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-off-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Recent Dent Repairs" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Remove Those Dents?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free dent assessment. We'll recommend the best repair method for your vehicle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DentRepair;
